import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useRecipeForm } from '../hooks/useRecipeForm';
import IngredientsEditor from '../components/editor/IngredientsEditor';
import StepsEditor from '../components/editor/StepsEditor';
import RecipeMeta from '../components/editor/RecipeMeta';
import { formatTime, parseTimeInput } from '../utils/time';
import './RecipeEditor.css';

const DIFFICULTIES = [
  { value: '',              label: '– Schwierigkeit –' },
  { value: 'Einfach',       label: 'Einfach' },
  { value: 'Mittel',        label: 'Mittel' },
  { value: 'Anspruchsvoll', label: 'Anspruchsvoll' },
];


function RecipeEditor({ recipeId: recipeIdProp, onSaved, initialData } = {}) {
  const { id: urlId } = useParams();
  const id = recipeIdProp ?? urlId;
  const navigate = useNavigate();
  const { user } = useAuth();

  const {
    formData,
    loading,
    rightsConfirmed, setRightsConfirmed,
    tagInput, setTagInput,
    imagePreview, imageInputRef,
    stepImagePreviews, stepImageInputRefs,
    dragOverIdx, canDrag,
    validationErrors, setValidationErrors,
    saveError,
    showDeleteConfirm, setShowDeleteConfirm,
    prepTimeRef, cookTimeRef,
    handleChange, stepServings,
    handleIngredientChange, addIngredient, removeIngredient,
    handleStepChange, handleStepImageChange, removeStepImage, addStep, removeStep,
    handleStepImagesChange,
    handleDragStart, handleDragOver, handleDrop, handleDragEnd,
    addTag, removeTag,
    handleImageChange,
    handleImageRemove,
    handleCoverCameraCapture,
    handleSubmit,
  } = useRecipeForm({ id, onSaved, navigate, initialData });

  const [showCoverCamera, setShowCoverCamera] = useState(false);
  const [hasCamera, setHasCamera] = useState(false);
  useEffect(() => {
    const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
    setHasCamera(isMobile);
  }, []);

  // ── Gemeinsame Blöcke ─────────────────────────────────────────

  const basicInfoSection = (
    <div className="editor-section">
      <div className="editor-section-head">Grundinformationen</div>
      <div className="editor-section-body">
        <div className="editor-field">
          <label className="field-label">Rezeptname *</label>
          <input
            type="text"
            className="field-input"
            style={{ fontFamily: 'var(--font-serif)', fontSize: '1.05rem', fontWeight: 600 }}
            required
            value={formData.title}
            placeholder="Rezeptname eingeben …"
            onChange={e => handleChange('title', e.target.value)}
          />
        </div>
        <div className="editor-field">
          <label className="field-label">Kurzbeschreibung (optional)</label>
          <textarea
            className="field-input field-textarea"
            placeholder="Was macht dieses Rezept besonders?"
            value={formData.description}
            onChange={e => handleChange('description', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  const basicDataSection = (
    <div className="editor-section">
      <div className="editor-section-head">Grunddaten</div>
      <div className="editor-section-body">
        <div className="field-row" style={{ marginBottom: 12 }}>
          <div className="editor-field">
            <label className="field-label">Vorbereitungszeit * <span style={{fontWeight:'normal',color:'var(--txt3)'}}>Minuten</span></label>
            <input
              ref={prepTimeRef}
              type="number"
              min="1"
              className={'field-input' + (validationErrors.prep_time ? ' field-error' : '')}
              placeholder="z.B. 10"
              value={formData.prep_time || ''}
              onChange={e => { handleChange('prep_time', parseTimeInput(e.target.value)); setValidationErrors(prev => ({ ...prev, prep_time: null })); }}
            />
            {validationErrors.prep_time && <span className="field-error-msg">{validationErrors.prep_time}</span>}
            {formData.prep_time > 0 && <span style={{fontSize:'0.8rem',color:'var(--txt3)'}}>{formatTime(formData.prep_time)}</span>}
          </div>
          <div className="editor-field">
            <label className="field-label">Zubereitungszeit * <span style={{fontWeight:'normal',color:'var(--txt3)'}}>Minuten</span></label>
            <input
              ref={cookTimeRef}
              type="number"
              min="1"
              className={'field-input' + (validationErrors.cook_time ? ' field-error' : '')}
              placeholder="z.B. 20"
              value={formData.cook_time || ''}
              onChange={e => { handleChange('cook_time', parseTimeInput(e.target.value)); setValidationErrors(prev => ({ ...prev, cook_time: null })); }}
            />
            {validationErrors.cook_time && <span className="field-error-msg">{validationErrors.cook_time}</span>}
            {formData.cook_time > 0 && <span style={{fontSize:'0.8rem',color:'var(--txt3)'}}>{formatTime(formData.cook_time)}</span>}
          </div>
        </div>
        <div className="field-row">
          <div className="editor-field">
            <label className="field-label">Portionen</label>
            <div className="portion-field">
              <button type="button" className="port-f-btn" onClick={() => stepServings(-1)}>−</button>
              <input className="port-f-input" type="number" value={formData.servings} readOnly />
              <button type="button" className="port-f-btn" onClick={() => stepServings(1)}>+</button>
            </div>
          </div>
          <div className="editor-field">
            <label className="field-label">Schwierigkeit</label>
            <select
              className="field-select"
              value={formData.difficulty}
              onChange={e => handleChange('difficulty', e.target.value)}
            >
              {DIFFICULTIES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const ingredientsSection = (
    <IngredientsEditor
      ingredients={formData.ingredients}
      servings={formData.servings}
      onIngredientChange={handleIngredientChange}
      onAdd={addIngredient}
      onRemove={removeIngredient}
    />
  );

  const stepsSection = (
    <StepsEditor
      steps={formData.steps}
      stepImagePreviews={stepImagePreviews}
      stepImageInputRefs={stepImageInputRefs}
      dragOverIdx={dragOverIdx}
      canDrag={canDrag}
      onStepChange={handleStepChange}
      onStepImageChange={handleStepImageChange}
      onRemoveStepImage={removeStepImage}
      onAddStep={addStep}
      onRemoveStep={removeStep}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onDragEnd={handleDragEnd}
      onStepImagesChange={handleStepImagesChange}
    />
  );

  // ── Inline-Modus (nach Scan) ──────────────────────────────────
  if (onSaved) {
    return (
      <div className="editor-page fade-in editor-inline">
        <form id="recipe-form-inline" onSubmit={handleSubmit}>
          <div className="editor-grid">

            {/* ── Linke Spalte ── */}
            <div className="editor-main">
              {basicInfoSection}
              {basicDataSection}
              {ingredientsSection}
              {stepsSection}
            </div>

            {/* ── Rechte Sidebar ── */}
            <RecipeMeta
              formData={formData}
              handleChange={handleChange}
              tagInput={tagInput}
              setTagInput={setTagInput}
              addTag={addTag}
              removeTag={removeTag}
              recipeId={id}
              showDeleteConfirm={showDeleteConfirm}
              setShowDeleteConfirm={setShowDeleteConfirm}
              navigate={navigate}
              user={user}
              imagePreview={imagePreview}
              imageInputRef={imageInputRef}
              handleImageChange={handleImageChange}
              handleImageRemove={handleImageRemove}
              handleCoverCameraCapture={handleCoverCameraCapture}
              showCoverCamera={showCoverCamera}
              setShowCoverCamera={setShowCoverCamera}
              hasCamera={hasCamera}
              footer={
                <div className="sidebar-panel">
                  <label className="rights-confirm-box">
                    <input type="checkbox" checked={rightsConfirmed}
                      onChange={e => setRightsConfirmed(e.target.checked)} />
                    <span>
                      Ich bestätige, dass ich die Rechte an diesem Rezept besitze oder die ausdrückliche Erlaubnis habe, es hier zu speichern und zu veröffentlichen.
                      <span className="rights-confirm-required"> (Pflichtfeld)</span>
                    </span>
                  </label>
                  {saveError && <div className="error-message" style={{ marginTop: '0.5rem' }}>{saveError}</div>}
                  <button type="submit" className="btn btn-primary"
                    disabled={loading || !rightsConfirmed}
                    title={!rightsConfirmed ? 'Bitte zuerst die Rechte-Bestätigung anhaken' : ''}
                    style={{ width: '100%', marginTop: '0.75rem' }}>
                    {loading ? 'Speichert...' : 'Speichern'}
                  </button>
                </div>
              }
            />

          </div>
        </form>
      </div>
    );
  }

  // ── Standalone-Modus (eigene Seite) ──────────────────────────
  return (
    <div className="editor-page fade-in">

      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">
            {id ? <>Rezept <em>bearbeiten</em></> : <>Rezept <em>erfassen</em></>}
          </div>
          <div className="page-sub">
            {formData.title ? formData.title : 'Entwurf · nicht gespeichert'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div style={{ fontSize: '0.75rem', color: 'var(--txt3)', display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--amber)' }}></div>
            Entwurf
          </div>
          <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Abbrechen</button>
          <button type="submit" form="recipe-form" className="btn btn-primary" disabled={loading}>
            {loading ? 'Speichert...' : id ? 'Speichern' : 'Veröffentlichen'}
          </button>
        </div>
      </div>

      <form id="recipe-form" onSubmit={handleSubmit}>
        <div className="editor-grid">

          {/* ── Linke Spalte ── */}
          <div className="editor-main">
            {basicInfoSection}
            {basicDataSection}
            {ingredientsSection}
            {stepsSection}
          </div>

          {/* ── Rechte Sidebar ── */}
          <RecipeMeta
            formData={formData}
            handleChange={handleChange}
            tagInput={tagInput}
            setTagInput={setTagInput}
            addTag={addTag}
            removeTag={removeTag}
            recipeId={id}
            showDeleteConfirm={showDeleteConfirm}
            setShowDeleteConfirm={setShowDeleteConfirm}
            navigate={navigate}
            user={user}
            imagePreview={imagePreview}
            imageInputRef={imageInputRef}
            handleImageChange={handleImageChange}
            handleImageRemove={handleImageRemove}
            handleCoverCameraCapture={handleCoverCameraCapture}
            showCoverCamera={showCoverCamera}
            setShowCoverCamera={setShowCoverCamera}
            hasCamera={hasCamera}
          />

          {/* AGB */}
          <div className="agb-bar">
            <p className="agb-text">
              Mit dem Speichern bestätigst du, dass du unsere <a href="/agb">Allgemeinen Geschäftsbedingungen</a> akzeptierst und die Rechte an allen hochgeladenen Inhalten besitzt.
            </p>
          </div>

        </div>
      </form>
    </div>
  );
}

export default RecipeEditor;
